import { restartableTask } from 'ember-concurrency';
import moment from 'moment-timezone';
import Task, {
  indexedEntities,
  TaskResourceNameFromParentRecordResourceName
} from 'volta/models/task';
import TaskService from 'volta/services/task-service';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { eqFilter, inFilter } from 'volta/utils/filters-utils';

import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import { next as runNext } from '@ember/runloop';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { IJsonApiQuery, SingleResourceDoc } from 'volta/utils/api/jsonapi-types';
import type { IResourceName } from 'volta/models/base-model';
import type EventStreamService from 'volta/services/event-stream';
import type { UTask } from 'volta/models/types/tasks';
import type IntlService from 'ember-intl/services/intl';

interface IArgs {
  entityId?: string;
  resourceName?: IResourceName;
  isLoading?: boolean;
  comments: UTask[];
  showWithEntity?: boolean;
  sortProperties?: string[];
}

export default class CommentKit extends Component<IArgs> {
  @service eventStream!: EventStreamService;
  @service taskService!: TaskService;
  @service intl!: IntlService;

  @tracked comments: UTask[] = this.args.comments ?? [];

  get taskResourceName() {
    return TaskResourceNameFromParentRecordResourceName(this.args.resourceName);
  }

  get commentsCount(): number {
    return this.comments.length;
  }

  commentsSortProperties = this.args.sortProperties ?? ['createdAt:desc'];

  @sort('comments', 'commentsSortProperties')
  sortedComments!: UTask[];

  get indexedEntities() {
    return indexedEntities(this.comments);
  }

  @cached
  get formattedComments() {
    const formattedComments = this.sortedComments.reduce(
      (arr: UTask[], comment, index, comments) => {
        // To reset state when refreshing
        comment.showCommentAuthor = false;
        const next = arr[0] || ({} as Task); // the next is the first of the built array, since we prepend
        if (next) {
          next.isFirstComment = false;
        }

        // Not the same author || same author but different day
        const commentDisplay =
          comment?.createdBy?.login !== next.createdBy?.login ||
          (comment?.createdBy?.login === next?.createdBy?.login &&
            moment(comment.createdAt).diff(next.createdAt, 'days') !== 0);

        if (!next || commentDisplay) {
          runNext(() => {
            comment.showCommentAuthor = true;
          });
        }

        if (next && commentDisplay) {
          next.isFirstComment = true;
        }

        if (index === comments.length - 1) {
          comment.isFirstComment = true;
        }

        return [comment].concat(arr);
      },
      []
    );

    if (this.args.showWithEntity) {
      return TaskService.indexTasksByEntity(formattedComments);
    }
    return TaskService.indexTasksByDate(formattedComments, this.intl.t('tasksPage.noDueDate'),'createdAt');
  }

  @action
  handleCommentCreated(data: SingleResourceDoc) {
    const { entityId } = this.args;
    if (this.isDestroyed || this.isDestroying || !data || !this.taskResourceName || !entityId) {
      return;
    }

    const comment = normalize(this.taskResourceName.singular, data) as UTask;

    if (comment && comment.linkedEntityId === entityId && comment.taskType === 'COMMENT') {
      this.addComment(comment);
    }
  }

  @action
  handleCommentDeleted({ data }: SingleResourceDoc) {
    const { entityId } = this.args;
    if (this.isDestroyed || this.isDestroying || !data.id || !entityId) {
      return;
    }

    if (data.attributes?.linkedEntityId === entityId && data.attributes.taskType === 'COMMENT') {
      this.removeComment(data.id);
    }
  }

  deleteComment = restartableTask(async (comment: UTask) => {
    try {
      await this.taskService.deleteTask.perform(comment);
      this.removeComment(comment.id);
    } catch (error) {
      defaultErrorHandler(error);
    }
  });

  fetchComments = restartableTask(async () => {
    const { entityId } = this.args;
    if (entityId && this.taskResourceName) {
      this.comments = await this.taskService.fetchCommentTasks.perform(
        entityId,
        this.args.resourceName!
      );
    } else {
      const query: IJsonApiQuery = {
        filter: {
          taskType: inFilter(['COMMENT'])
        },
        sort: 'createdAt'
      };

      if (entityId) {
        query.filter!.linkedEntityId = eqFilter(entityId);
      }

      this.comments = (await this.taskService.fetchTasks.perform(query)).toArray();
    }
  });

  @action
  removeComment(commentId: string) {
    this.comments = this.comments.filter((c) => c.id !== commentId);
  }

  @action
  addComment(comment: UTask) {
    if (!!this.comments.find((c) => c.id === comment.id)) {
      return;
    }

    this.comments = [...this.comments, comment];
  }
}
